import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

// User-pinned projects and locations. Persisted in localStorage — no
// backend roundtrip, lost on browser change. Cross-Device-Sync is out of
// scope for the first cut.

export type PinKind = 'project' | 'location'

const STORAGE_KEY = 'revodata:pinned'

type Persisted = {
	projects: string[]
	locations: string[]
}

const loadFromStorage = (): Persisted => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return { projects: [], locations: [] }
		const parsed = JSON.parse(raw)
		return {
			projects: Array.isArray(parsed?.projects) ? parsed.projects.filter((x: any) => typeof x === 'string') : [],
			locations: Array.isArray(parsed?.locations)
				? parsed.locations.filter((x: any) => typeof x === 'string')
				: [],
		}
	} catch {
		return { projects: [], locations: [] }
	}
}

export const usePinnedStore = defineStore('pinned', () => {
	const initial = loadFromStorage()
	const projectIds = ref<string[]>(initial.projects)
	const locationIds = ref<string[]>(initial.locations)

	const pinnedProjects = computed(() => projectIds.value)
	const pinnedLocations = computed(() => locationIds.value)
	const isEmpty = computed(() => projectIds.value.length === 0 && locationIds.value.length === 0)

	const isPinned = (kind: PinKind, id: string): boolean => {
		if (!id) return false
		return kind === 'project' ? projectIds.value.includes(id) : locationIds.value.includes(id)
	}

	const togglePin = (kind: PinKind, id: string) => {
		if (!id) return
		const list = kind === 'project' ? projectIds : locationIds
		const idx = list.value.indexOf(id)
		if (idx === -1) list.value = [...list.value, id]
		else list.value = list.value.filter((x) => x !== id)
	}

	// Persist on any change.
	watch(
		[projectIds, locationIds],
		([p, l]) => {
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify({ projects: p, locations: l }))
			} catch {
				// Storage quota or disabled — silently no-op, in-memory state is still usable.
			}
		},
		{ deep: true },
	)

	return {
		pinnedProjects,
		pinnedLocations,
		isEmpty,
		isPinned,
		togglePin,
	}
})
